/* The app shell. Phase 1 routes mirror the live app's surfaces so the migration can be verified
   page by page against the same data:

     /                    landing (marketing)
     /app                 the organizer's account and pages          (new in Phase 1)
     /app#office          the back office (superadmins)
     /:slug               a page's contribution form
     /:slug#admin         that page's dashboard
     /:slug#reveal=token  the reveal (with &trailer=1 for trailer-only)
     /:word               a short link

   Each route is a placeholder until its surface is ported. */
import { Suspense, lazy, useEffect, useState } from "react";
/* UX audit follow-up (2026-10-10): every surface except the landing, the /later feature page and the slug gate is a
   route-level chunk, so a homepage or recipient-link visitor only downloads the shell. Named exports are re-wrapped as
   defaults for React.lazy; Suspense falls back to the same Splash the slug gate already shows. */
const RevealPage = lazy(() => import("@/features/reveal/RevealPage").then((m) => ({ default: m.RevealPage })));
const SubmitPage = lazy(() => import("@/features/submit/SubmitPage").then((m) => ({ default: m.SubmitPage })));
const AdminShell = lazy(() => import("@/features/admin/AdminShell").then((m) => ({ default: m.AdminShell })));
const ReactionCapture = lazy(() => import("@/features/admin/ReactionCapture").then((m) => ({ default: m.ReactionCapture })));
const AuthCallback = lazy(() => import("@/features/admin/AuthCallback").then((m) => ({ default: m.AuthCallback })));
const EditPage = lazy(() => import("@/features/submit/EditPage").then((m) => ({ default: m.EditPage })));
const RemotePage = lazy(() => import("@/features/reveal/RemotePage").then((m) => ({ default: m.RemotePage })));
const CreateWizard = lazy(() => import("@/features/landing/CreateWizard").then((m) => ({ default: m.CreateWizard })));
const PagesList = lazy(() => import("@/features/admin/PagesList").then((m) => ({ default: m.PagesList })));
const UnsubscribePage = lazy(() => import("@/features/landing/Unsubscribe").then((m) => ({ default: m.UnsubscribePage })));
const HelpPage = lazy(() => import("@/features/landing/Help").then((m) => ({ default: m.HelpPage })));
const AboutPage = lazy(() => import("@/features/landing/Legal").then((m) => ({ default: m.AboutPage })));
const PrivacyPage = lazy(() => import("@/features/landing/Legal").then((m) => ({ default: m.PrivacyPage })));
const TermsPage = lazy(() => import("@/features/landing/Legal").then((m) => ({ default: m.TermsPage })));
const GiftPage = lazy(() => import("@/features/landing/GiftPage").then((m) => ({ default: m.GiftPage })));
const TeamsPage = lazy(() => import("@/features/landing/Teams").then((m) => ({ default: m.TeamsPage })));
const OccasionPage = lazy(() => import("@/features/landing/OccasionPage").then((m) => ({ default: m.OccasionPage })));
const ComparePage = lazy(() => import("@/features/landing/ComparePage").then((m) => ({ default: m.ComparePage })));
const OccasionsHub = lazy(() => import("@/features/landing/OccasionPage").then((m) => ({ default: m.OccasionsHub })));
const GuidePage = lazy(() => import("@/features/landing/GuidePage").then((m) => ({ default: m.GuidePage })));
const OptinPage = lazy(() => import("@/features/landing/OptinPage").then((m) => ({ default: m.OptinPage })));
const StatusPage = lazy(() => import("@/features/landing/Status").then((m) => ({ default: m.StatusPage })));
const BackOffice = lazy(() => import("@/features/admin/BackOffice").then((m) => ({ default: m.BackOffice })));
const DevDemo = lazy(() => import("./DevDemo").then((m) => ({ default: m.DevDemo })));
const TextRemindersPage = lazy(() => import("@/features/landing/TextReminders").then((m) => ({ default: m.TextRemindersPage })));
const LaterCreate = lazy(() => import("@/features/later/LaterCreate").then((m) => ({ default: m.LaterCreate })));
const LaterRecipientPage = lazy(() => import("@/features/later/LaterRecipientPage").then((m) => ({ default: m.LaterRecipientPage })));
const VerifierPage = lazy(() => import("@/features/later/VerifierPage").then((m) => ({ default: m.VerifierPage })));
const LaterGift = lazy(() => import("@/features/later/LaterGift").then((m) => ({ default: m.LaterGift })));
const LaterReport = lazy(() => import("@/features/later/LaterReport").then((m) => ({ default: m.LaterReport })));
import { APP, OCCASIONS, fmtSecs } from "@/core";
import { parseLocation, type Route } from "./routes";
import { NotFoundPage, ShortLinkGate, Splash, type ShortInfo } from "@/features/reveal/ShortLinkGate";
import { getLanding, openShort, shortInfo, supabase } from "@/data/supabase";
import { keepFeedback } from "@/data/feedback";
import { Landing } from "@/features/landing/Landing";
import { IcoClapper } from "@/features/landing/icons";
import { LaterPage } from "@/features/later/LaterPage";
import { captureTouch } from "@/data/touch";
import { siteTrack } from "@/lib/siteTrack";

/* Where a visit can carry a source worth keeping (docs/MARKETING-HUB.md Phase 0): the public marketing surfaces. A contribution
   form, a reveal or a dashboard is reached by a private link, never by a campaign, so those loads leave the stored touch alone. */
export const TOUCH_ROUTES = new Set(["landing", "create", "laterpage", "latercreate", "latergift", "teams", "gift", "occasions", "occasion", "compare", "guide", "textreminders", "help", "about"]);

function useRoute(): Route {
  const [r, setR] = useState(() => parseLocation(window.location));
  useEffect(() => {
    const f = () => setR(parseLocation(window.location));
    const canon = () => {   // Round 2 R13: one canonical per path. The bare shell ships without one (prerendered pages carry their own), so create it when missing.
      let l = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
      if (!l) { l = document.createElement("link"); l.rel = "canonical"; document.head.appendChild(l); }
      l.href = "https://keepsakepage.com" + (window.location.pathname === "/" ? "/" : window.location.pathname.replace(/\/$/, ""));
    };
    canon(); window.addEventListener("popstate", canon);
    window.addEventListener("hashchange", f);
    window.addEventListener("popstate", f);
    return () => { window.removeEventListener("hashchange", f); window.removeEventListener("popstate", f); };
  }, []);
  return r;
}

export function App() {
  return <Suspense fallback={<Splash />}><AppInner /></Suspense>;
}

function AppInner() {
  const route = useRoute();
  useEffect(() => {
    if (!TOUCH_ROUTES.has(route.kind)) return;
    const pair = captureTouch(location.search, document.referrer, location.pathname);
    const code = new URLSearchParams(location.search).get("via"); if (code && pair?.last?.code) supabase().rpc("partner_touch", { p_code: pair.last.code }).then(() => undefined, () => undefined);   // a partner's link: count the visit (0148)
    try { if (!sessionStorage.getItem("ksp_visit")) { sessionStorage.setItem("ksp_visit", "1"); siteTrack("visit", {}); } } catch { siteTrack("visit", {}); }   // one visit per session, with the touch label (By source)
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps -- once per load: the referrer only means something on the first page
  // The reveal is a page of its own (full-width hero, no shell chrome), as on the live site.
  if (route.kind === "reveal") return <RevealPage slug={route.slug} token={route.token} trailerOnly={route.trailerOnly} prep={route.prep} />;
  if (route.kind === "landing") return <Landing />;
  if (route.kind === "create") return <CreateWizard />;
  if (route.kind === "laterpage") return <LaterPage />;   // a full-width page, like the landing
  if (route.kind === "latercreate") return <LaterCreate />;   // Round 3 R6: the inner pages carry the site header + footer themselves
  if (route.kind === "latergift") return <LaterGift />;
  if (route.kind === "teams") return <TeamsPage />;
  if (route.kind === "gift") return <GiftPage />;
  if (route.kind === "occasions") return <OccasionsHub />;
  if (route.kind === "occasion") return <OccasionPage slug={route.slug} later={!!route.later} />;
  if (route.kind === "compare") return <ComparePage slug={route.slug} />;
  if (route.kind === "guide") return <GuidePage slug={route.slug} />;
  if (route.kind === "optin") return <OptinPage kind={route.which} />;
  return (
    <main className="shell">
      <header className="shell-head">
        <span className="brand"><IcoClapper size={20} /> KeepSakePage</span>
      </header>
      {route.kind === "submit" ? (
        <SlugGate slug={route.slug} />
      ) : route.kind === "admin" ? (
        <AdminShell slug={route.slug} />
      ) : route.kind === "reaction" ? (
        <ReactionCapture slug={route.slug} />
      ) : route.kind === "auth" ? (
        <AuthCallback to={route.to} />
      ) : route.kind === "edit" ? (
        <EditPage slug={route.slug} token={route.token} />
      ) : route.kind === "remote" ? (
        <RemotePage slug={route.slug} token={route.token} />
      ) : route.kind === "account" ? (
        <PagesList />
      ) : route.kind === "help" ? (
        <HelpPage />
      ) : route.kind === "privacy" ? (
        <PrivacyPage />
      ) : route.kind === "terms" ? (
        <TermsPage />
      ) : route.kind === "about" ? (
        <AboutPage />
      ) : route.kind === "textreminders" ? (
        <TextRemindersPage />
      ) : route.kind === "laterreport" ? (
        <LaterReport />
      ) : route.kind === "keepsake" ? (
        <LaterRecipientPage slug={route.slug} token={route.token} />
      ) : route.kind === "verify" ? (
        <VerifierPage slug={route.slug} token={route.token} />
      ) : route.kind === "status" ? (
        <StatusPage />
      ) : route.kind === "unsubscribe" ? (
        <UnsubscribePage />
      ) : route.kind === "office" ? (
        <BackOffice />
      ) : route.kind === "dev" && import.meta.env.DEV ? (
        <DevDemo which={route.which} />
      ) : (
      <section className="card">
        <h1>KeepSakePage</h1>
        <p className="muted">Nothing at this address. <a href="/">Home</a></p>
        {import.meta.env.DEV && <CoreStatus />}
      </section>
      )}
    </main>
  );
}

/* Resolve /<word> the way the live PageGate does: a registered page renders its contribution
   form; anything else may be a short link (open it, or ask for its password); otherwise 404. */
function SlugGate({ slug }: { slug: string }) {
  const [state, setState] = useState<{ kind: "checking" } | { kind: "ok"; slug: string } | { kind: "short"; info: ShortInfo } | { kind: "missing" }>({ kind: "checking" });
  useEffect(() => {
    let live = true; setState({ kind: "checking" });
    getLanding(slug).then((l) => { if (!live) return; const cur = String((l as { slug?: string }).slug || slug); if (cur !== slug) { try { history.replaceState(null, "", "/" + cur + location.search + location.hash); } catch { /* */ } } setState({ kind: "ok", slug: cur }); }).catch(() => {   // a renamed page: the old address still resolves (0068); tidy the URL to the current one
      shortInfo(slug).then((info) => { if (!live) return;
        if (!info.protected) { openShort(slug, "").then((r) => { location.replace(keepFeedback(APP.revealPath(r) as string, location.search)); }).catch(() => setState({ kind: "short", info })); }
        else setState({ kind: "short", info }); })
        .catch(() => { if (live) setState({ kind: "missing" }); }); });
    return () => { live = false; };
  }, [slug]);
  if (state.kind === "checking") return <Splash />;
  if (state.kind === "short") return <ShortLinkGate word={slug} info={state.info} />;
  if (state.kind === "missing") return <NotFoundPage />;
  return <SubmitPage slug={state.slug} />;
}

/* Proof that the legacy core is loaded and typed: a few live reads from it. */
function CoreStatus() {
  const occasions = Object.keys(OCCASIONS);
  const exported = Object.keys(APP).length;
  return (
    <div className="status">
      <div><b>{exported}</b> exports in the logic core</div>
      <div><b>{occasions.length}</b> occasions: {occasions.join(", ")}</div>
      <div>fmtSecs(1163) = <b>{fmtSecs(1163)}</b></div>
    </div>
  );
}
